import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
} from '@mui/material';
import { apiService } from '../services/api';

interface Entity {
  id: string;
  name: string;
  code: string;
}

interface CreatePerformanceCycleModalProps {
  open: boolean;
  onClose: () => void;
  onCycleCreated: () => void;
}

const CreatePerformanceCycleModal: React.FC<CreatePerformanceCycleModalProps> = ({
  open,
  onClose,
  onCycleCreated,
}) => {
  const [formData, setFormData] = useState({
    entityId: '',
    name: '',
    year: new Date().getFullYear(),
    periodStartDate: '',
    periodEndDate: '',
    selfReviewDeadline: '',
    managerReviewDeadline: '',
    description: '',
  });

  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadEntities();
      generateDefaultDates();
    }
  }, [open]);

  const loadEntities = async () => {
    try {
      setDataLoading(true);
      const entitiesData = await apiService.getEntities();
      setEntities(entitiesData);
    } catch (err) {
      console.error('Error loading entities:', err);
      setError('Failed to load entities');
    } finally {
      setDataLoading(false);
    }
  };

  const generateDefaultDates = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const startDate = new Date(currentYear, 0, 1).toISOString().split('T')[0]; // Jan 1
    const endDate = new Date(currentYear, 11, 31).toISOString().split('T')[0]; // Dec 31
    const selfDeadline = new Date(currentYear + 1, 0, 31).toISOString().split('T')[0]; // Jan 31 next year
    const managerDeadline = new Date(currentYear + 1, 1, 28).toISOString().split('T')[0]; // Feb 28 next year

    setFormData(prev => ({
      ...prev,
      name: `${currentYear} Annual Review`,
      periodStartDate: startDate,
      periodEndDate: endDate,
      selfReviewDeadline: selfDeadline,
      managerReviewDeadline: managerDeadline,
    }));
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const cycleData = {
        ...formData,
        status: 'draft',
      };

      await apiService.createPerformanceCycle(cycleData);

      onCycleCreated();
      onClose();
      setFormData({
        entityId: '',
        name: '',
        year: new Date().getFullYear(),
        periodStartDate: '',
        periodEndDate: '',
        selfReviewDeadline: '',
        managerReviewDeadline: '',
        description: '',
      });
    } catch (err: any) {
      console.error('Error creating performance cycle:', err);
      setError(err.message || 'Failed to create performance cycle');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.entityId && formData.name && formData.periodStartDate && 
                     formData.periodEndDate && formData.selfReviewDeadline && 
                     formData.managerReviewDeadline;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Performance Cycle</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dataLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <Autocomplete
                options={entities}
                getOptionLabel={(option) => option.name}
                value={entities.find(e => e.id === formData.entityId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('entityId', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Entity"
                    required
                    helperText="Select company entity"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid size={8}>
              <TextField
                fullWidth
                label="Cycle Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., 2024 Annual Review"
                required
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={formData.year}
                onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Period Start Date"
                type="date"
                value={formData.periodStartDate}
                onChange={(e) => handleInputChange('periodStartDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Performance period start"
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Period End Date"
                type="date"
                value={formData.periodEndDate}
                onChange={(e) => handleInputChange('periodEndDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.periodStartDate }}
                helperText="Performance period end"
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Self Review Deadline"
                type="date"
                value={formData.selfReviewDeadline}
                onChange={(e) => handleInputChange('selfReviewDeadline', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Employee self-review deadline"
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Manager Review Deadline"
                type="date"
                value={formData.managerReviewDeadline}
                onChange={(e) => handleInputChange('managerReviewDeadline', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.selfReviewDeadline }}
                helperText="Manager review deadline"
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description for this performance cycle"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !isFormValid || dataLoading}
        >
          {loading ? <CircularProgress size={20} /> : 'Create Cycle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePerformanceCycleModal;